import { FC, useState, lazy, Suspense } from "react";
const VerificationTemplate = lazy(() => import("../../"));
import {
  Template,
  VerificationSchema
} from "types/landlord/verificationTemplate";
import { Modal } from "shared/components";
import { SideBar } from "../../components/SideBar";
import { LayoutFooter } from "../../components/LayoutFooter";
import { toast } from "react-toastify";
import { LayoutHeader } from "../../components/LayoutHeader";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Loader } from "shared/components/Loader";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { validateTemplate } from "../../validations";

type CreateTemplateModalProps = {
  opened: boolean;
  template?: Template | null;
  defaultTemplate?: Template;
  verificationSchemas: VerificationSchema[];
  handleCancel: () => void;
  handleSave: (template: Template) => void;
  onTemplateUpdate: (template: Template) => void;
};

export const CreateTemplateModal: FC<CreateTemplateModalProps> = ({
  opened,
  template,
  defaultTemplate,
  verificationSchemas,
  handleCancel,
  handleSave,
  onTemplateUpdate
}) => {
  const [error, setError] = useState<string>();

  if (!template || !defaultTemplate) return null;

  const updateName = (value: string) => {
    const updatedTemplate = {
      ...template,
      name: value
    };
    onTemplateUpdate(updatedTemplate);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      panelClassName="relative mx-auto h-[90dvh] w-[90dvw] overscroll-none bg-background-900 rounded-lg overflow-hidden"
    >
      <Suspense fallback={<Loader />}>
        <VerificationTemplate
          layoutProps={{
            header: (
              <>
                <div className="flex p-4 border-b border-alpha-40 justify-end rounded-tr-lg bg-background-1000">
                  <IconButton
                    variant={IconButtonVariant.TEXT_BASE}
                    size={IconButtonSize.SM}
                    onClick={handleCancel}
                    HugeIcon={Cancel01Icon}
                  />
                </div>
                <LayoutHeader />
              </>
            ),
            sidebar: (
              <SideBar
                isModal
                error={error}
                clearError={() => setError("")}
                template={template}
                editedName={template.name}
                setEditedName={updateName}
              />
            ),
            footer: (
              <LayoutFooter
                onCancel={handleCancel}
                onSave={(newTemplate) => {
                  if (!newTemplate.name) {
                    setError("Template name is required");
                    toast.error("Template name is required");
                    return;
                  }

                  const validationResult = validateTemplate(newTemplate, {
                    onError: (message, type) => {
                      if (type === "warning") toast.warning(message);
                      else toast.error(message);
                    }
                  });

                  if (!validationResult.isValid) return;

                  handleSave(newTemplate);
                }}
              />
            )
          }}
          isModal
          verificationTemplate={template}
          savedTemplate={defaultTemplate}
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          verificationSchemas={verificationSchemas || []}
          onTemplateUpdate={onTemplateUpdate}
          createMode={true}
          originalQualificationsDisclosureText=""
          originalSubmissionDisclosureText=""
        />
      </Suspense>
    </Modal>
  );
};
